/**
 * udmUtils.js — Utility per la gestione dell'Unità di Misura (UDM)
 *
 * Regole di normalizzazione:
 *   g      → prezzo €/kg  (÷1000 per quantità, ×1000 per prezzo)
 *   kg     → prezzo €/kg  (1:1)
 *   ml     → prezzo €/L   (÷1000 per quantità, ×1000 per prezzo)
 *   litri  → prezzo €/L   (1:1)
 *   pz / fetta / bustina → prezzo €/pz (1:1, nessuna conversione)
 */

/** Etichetta leggibile per ogni UDM */
export const labelUdm = (udm) => {
  const u = (udm || "").toLowerCase();
  const map = {
    kg: "kg", g: "g",
    litri: "L", litro: "L", l: "L", lt: "L", ml: "ml",
    pz: "pz", fetta: "fetta", bustina: "bustina",
  };
  return map[u] || udm || "kg";
};

/** Etichetta del prezzo normalizzato (€/kg, €/L, €/pz …) */
export const prezzoBaseLabel = (udm) => {
  if (!udm) return "€/kg";
  if (udm === "g" || udm === "kg") return "€/kg";
  if (udm === "ml" || udm === "litri") return "€/L";
  return `€/${labelUdm(udm)}`;
};

/** Famiglia dell'UDM (fonte di verità per classificare un prodotto) */
export const isLiquidoUdm = (udm) => ["ml", "l", "lt", "litri", "litro"].includes((udm || "").toLowerCase());
export const isPezziUdm = (udm) => ["pz", "fetta", "bustina", "confezione", "pezzo", "pezzi"].includes((udm || "").toLowerCase());
export const udmKind = (udm) => (isPezziUdm(udm) ? "pezzi" : isLiquidoUdm(udm) ? "liquido" : "peso");

/**
 * udmStorageFactor — fattore per convertire un valore inserito NELL'UDM DEL PRODOTTO
 * verso l'unità "piccola" usata internamente per lo storage e il calcolo costi (g/ml/pz).
 *   kg → 1000 (1 kg = 1000 g)   |   litri → 1000 (1 L = 1000 ml)
 *   g, ml, pz/fetta/bustina → 1
 * Uso: valore_storage(g/ml) = valore_input(udm) * udmStorageFactor(udm)
 *      valore_input(udm)    = valore_storage(g/ml) / udmStorageFactor(udm)
 */
export const udmStorageFactor = (udm) => {
  const u = (udm || "").toLowerCase();
  if (u === "kg" || u === "litri" || u === "litro" || u === "l" || u === "lt") return 1000;
  return 1;
};

/**
 * Unità "piccola" in cui l'operatore inserisce SEMPRE il quantitativo in ricetta
 * (coerente con lo storage DB: tutto in g/ml/pezzi):
 *   - peso    → "g"
 *   - liquido → "ml"
 *   - pezzi   → label del prodotto (pz/fetta/bustina)
 */
export const udmInputLabel = (udm) => {
  const k = udmKind(udm);
  if (k === "liquido") return "ml";
  if (k === "pezzi") return labelUdm(udm);
  return "g";
};

/**
 * calcolaCostoIngrediente
 * IMPORTANTE: per coerenza con il flusso di inserimento (l'utente inserisce SEMPRE
 * in grammi/ml/pezzi), il valore `quantita` salvato è sempre nell'unità "piccola":
 *   - prodotto kg/g  → quantita in GRAMMI
 *   - prodotto l/ml  → quantita in MILLILITRI
 *   - prodotto pz/fetta/bustina → quantita in PEZZI
 * Il `prezzoBase` è normalizzato (€/kg per kg|g, €/L per litri|ml, €/pz per pz).
 */
export const calcolaCostoIngrediente = (quantita, prezzoBase, udm) => {
  const qty = parseFloat(quantita) || 0;
  const price = parseFloat(prezzoBase) || 0;
  if (qty === 0 || price === 0) return 0;

  const k = udmKind(udm);
  if (k === "pezzi") return qty * price;        // qty in pezzi, price €/pz
  return (qty / 1000) * price;                  // qty in g→€/kg oppure ml→€/L
};

/**
 * normalizzaPrezzoBase
 * Converte un prezzo di acquisto → prezzo normalizzato da salvare in DB (prezzo_kg)
 * Dato:
 *   prezzoAcquisto → es. €2.50 (per 500g)
 *   quantitaAcquistata → es. 500 (in UDM)
 *   udm → "g"
 * Restituisce:  prezzo_kg = €5.00/kg
 */
export const normalizzaPrezzoBase = (prezzoAcquisto, quantitaAcquistata, udm) => {
  const price = parseFloat(prezzoAcquisto) || 0;
  const qty = parseFloat(quantitaAcquistata) || 1;
  if (price === 0) return 0;

  // prezzo per singola unità nell'UDM
  const pricePerUnit = price / qty;

  switch (udm) {
    case "g":    return pricePerUnit * 1000;  // → €/kg
    case "kg":   return pricePerUnit;          // → €/kg
    case "ml":   return pricePerUnit * 1000;  // → €/L
    case "litri":return pricePerUnit;          // → €/L
    case "pz":
    case "fetta":
    case "bustina":
    default:     return pricePerUnit;          // → €/unit
  }
};

/**
 * Opzioni UDM per i prodotti Food & Beverage
 * NB: "confezione" è ESCLUSA per design
 */
export const UNITA_FOOD = [
  { value: "kg",     label: "kg — chilogrammi" },
  { value: "g",      label: "g — grammi" },
  { value: "litri",  label: "L — litri" },
  { value: "ml",     label: "ml — millilitri" },
  { value: "pz",     label: "pz — pezzi" },
  { value: "fetta",  label: "fetta" },
  { value: "bustina",label: "bustina" },
];

/**
 * formatGiacenza
 * Mostra la quantità in magazzino con l'unità di misura reale del prodotto,
 * convertendo automaticamente in unità più leggibili (es. 1500 g → "1.5 kg").
 */
export const formatGiacenza = (quantita, udm) => {
  const q = parseFloat(quantita) || 0;
  const k = udmKind(udm);
  // Liquidi: il valore è SEMPRE in ml
  if (k === "liquido") {
    return q >= 1000
      ? `${(q / 1000).toFixed(q % 1000 === 0 ? 0 : 2).replace(/\.?0+$/, "")} L`
      : `${q.toFixed(0)} ml`;
  }
  // Pezzi/fetta/bustina: numero intero/decimale + label
  if (k === "pezzi") {
    return `${q % 1 === 0 ? q.toFixed(0) : q.toFixed(2)} ${labelUdm(udm)}`;
  }
  // Peso (g/kg): il valore è SEMPRE in grammi
  return q >= 1000
    ? `${(q / 1000).toFixed(q % 1000 === 0 ? 0 : 2).replace(/\.?0+$/, "")} kg`
    : `${q.toFixed(0)} g`;
};

/**
 * Opzioni UDM per i Materiali (non-food)
 */
export const UNITA_MATERIALI = [
  { value: "pz",          label: "Pezzi" },
  { value: "scatola",     label: "Scatola" },
  { value: "rotolo",      label: "Rotolo" },
  { value: "kg",          label: "Kg" },
  { value: "litri",       label: "Litri" },
  { value: "pacco",       label: "Pacco" },
];
